/**
 * localProjects.js
 * All local-mode project logic in one place so Projects.js and
 * WelcomeLanding.js only need small edits.
 * Lives in src/utils/ next to apiTarget.js and localEngine.js.
 */

import { invoke } from '@tauri-apps/api/core';
import { activateLocal, activateCloud, isLocalMode } from './apiTarget';

const CLOUD_BASE = process.env.REACT_APP_API_URL || 'https://api.dataquadapp.com';

let engine = null; // { port, token, base_url }
let engineStarting = null;      // in-flight start promise (single-flight)
let engineFailedAt = 0;         // last failure time for cooldown
const ENGINE_RETRY_COOLDOWN_MS = 45000;
const localProjectIds = new Set();

function rememberLocalIds(projects) {
  for (const p of projects || []) {
    if (p && p.id && (p.storage_mode === 'local' || p.folder)) localProjectIds.add(String(p.id));
  }
}

export function isTauri() {
  return Boolean(window.__TAURI_INTERNALS__);
}

export async function startLocalEngine() {
  if (engine) return engine;

  // Single-flight: concurrent callers (WelcomeLanding, Projects, auto-resume,
  // StrictMode double effects) share ONE spawn instead of each starting
  // their own python process.
  if (engineStarting) return engineStarting;

  // Failure cooldown: if the engine just failed to start (for example the
  // machine is out of memory), fail fast instead of respawning python on
  // every caller and making the pressure worse.
  if (Date.now() - engineFailedAt < ENGINE_RETRY_COOLDOWN_MS) {
    throw new Error('Local engine unavailable (retry shortly)');
  }

  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  engineStarting = invoke('start_local_engine', { userEmail: userInfo?.email || null })
    .then((info) => {
      engine = info;
      engineFailedAt = 0;
      return info;
    })
    .catch((e) => {
      engineFailedAt = Date.now();
      throw e;
    })
    .finally(() => {
      engineStarting = null;
    });
  return engineStarting;
}

function engineHeaders() {
  return {
    'Authorization': `Bearer ${engine.token}`,
    // local_auth.py verifies entitlement with the CLOUD login JWT; the
    // Bearer slot carries the transport token, so the JWT rides here.
    'X-Cloud-Token': localStorage.getItem('token') || '',
    'Content-Type': 'application/json',
  };
}

export async function pickProjectFolder() {
  return await invoke('pick_folder', { title: 'Choose where this project will live' });
}

/**
 * Returns the local projects list mapped to the same shape as cloud
 * projects. Empty array outside Tauri or if the engine fails, so cloud
 * flows never break.
 */
export async function fetchLocalProjects(includeDeleted = true) {
  if (!isTauri()) return [];
  try {
    await startLocalEngine();
    const res = await fetch(
      `${engine.base_url}/api/projects?include_deleted=${includeDeleted}`,
      { headers: engineHeaders() }
    );
    if (!res.ok) return [];
    const data = await res.json();
    rememberLocalIds(data.projects);
    return data.projects || [];
  } catch (e) {
    console.error('Local projects fetch failed:', e);
    return [];
  }
}

export async function createLocalProject({ name, description, parentDir }) {
  await startLocalEngine();
  const res = await fetch(`${engine.base_url}/api/projects`, {
    method: 'POST',
    headers: engineHeaders(),
    body: JSON.stringify({ name, description: description || '', parent_dir: parentDir }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to create local project');
  }
  const p = await res.json();
  localProjectIds.add(String(p.id));
  return { ...p, storage_mode: 'local', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
}

export async function softDeleteLocalProject(projectId) {
  await startLocalEngine();
  const res = await fetch(`${engine.base_url}/api/projects/${projectId}/soft-delete`, {
    method: 'PATCH', headers: engineHeaders(),
  });
  if (!res.ok) throw new Error('Failed to close local project');
}

export async function restoreLocalProject(projectId) {
  await startLocalEngine();
  const res = await fetch(`${engine.base_url}/api/projects/${projectId}/restore`, {
    method: 'PATCH', headers: engineHeaders(),
  });
  if (!res.ok) throw new Error('Failed to restore local project');
}

export async function permanentDeleteLocalProject(projectId, removeFiles) {
  await startLocalEngine();
  const res = await fetch(
    `${engine.base_url}/api/projects/${projectId}?remove_files=${removeFiles ? 'true' : 'false'}`,
    { method: 'DELETE', headers: engineHeaders() }
  );
  if (!res.ok) throw new Error('Failed to delete local project');
}

/**
 * Call BEFORE onProjectSelect(project). Points dataService (and apiTarget)
 * at the right backend for this project. Cloud projects restore the cloud
 * base; local projects start the engine and switch to it.
 */
export async function activateProjectStorage(project) {
  const isLocal = project && project.storage_mode === 'local';
  if (isLocal) {
    localProjectIds.add(String(project.id));
    const e = await startLocalEngine();
    activateLocal(e.base_url, e.token);
    installEngineFetchInterceptor();
    await applyBaseEverywhere(e.base_url);
  } else {
    activateCloud();
    await applyBaseEverywhere(CLOUD_BASE);
  }
}

// Arm the interceptor as early as the module loads, so requests fired
// before the first activation are already covered.
if (typeof window !== 'undefined') {
  try { installEngineFetchInterceptor(); } catch (e) { /* ignore */ }
  // Warm start: if the app will auto-resume a LOCAL project, begin spawning
  // the engine NOW, in parallel with login/paint, instead of when the first
  // data request needs it. Turns the ~5s cold-start wait into background
  // work the user never feels. Fire-and-forget: failures fall back to the
  // normal on-demand start with its cooldown.
  try {
    const remembered = JSON.parse(localStorage.getItem('dq_current_project') || 'null');
    if (remembered && remembered.storage_mode === 'local' && isTauri()) {
      localProjectIds.add(String(remembered.id));
      startLocalEngine().catch(() => {});
    }
  } catch (e) { /* ignore */ }
}

/**
 * App.js and dataService build their auth headers from the CLOUD login
 * token in localStorage. The engine only accepts its own per-launch token.
 * Rather than editing dozens of fetch call sites, this wraps window.fetch
 * once: any request aimed at the engine gets its Authorization header
 * swapped for the engine token. Requests to any other origin (cloud API,
 * Paddle, fonts) pass through untouched.
 */
let fetchPatched = false;
function installEngineFetchInterceptor() {
  if (fetchPatched) return;
  fetchPatched = true;
  const originalFetch = window.fetch.bind(window);

  const referencesLocalProject = (url) => {
    for (const id of localProjectIds) {
      if (url.includes(id)) return true;
    }
    return false;
  };

  // Processing endpoints that carry the project id in the request BODY
  // rather than the URL (DataSummary, CollectorDashboard, detectors...).
  // While a local project is active these must hit the engine.
  const ENGINE_PATH_PREFIXES = [
    '/data-summary', '/run-quality-checks', '/run-backcheck-analysis',
    '/analyze-collectors', '/analyze-timeline', '/load-data', '/load-kobo',
    '/load-surveycto', '/load-odk', '/redetect-types', '/correlation',
    '/api/cleaning', '/api/export', '/api/xlsform', '/api/verification',
    '/api/reports', '/api/check', '/api/text',
  ];

  const isEngineProcessingPath = (url) => {
    const path = url.slice(CLOUD_BASE.length);
    return ENGINE_PATH_PREFIXES.some(p => path.startsWith(p));
  };

  window.fetch = (input, init) => {
    try {
      let url = typeof input === 'string' ? input : (input && input.url) || '';

      // Some components keep their own hardcoded cloud API_BASE_URL
      // (DataPreview and friends). Any cloud-bound request that references
      // a LOCAL project id is redirected to the engine, so those
      // components work without being edited.
      const targetsLocal = url.startsWith(CLOUD_BASE) &&
        (referencesLocalProject(url) || (isLocalMode() && isEngineProcessingPath(url)));

      // Race guard: during auto-resume, components can fire requests for a
      // local project while the engine is still spawning. Hold those until
      // the in-flight start resolves, then redirect, instead of letting
      // them leak to the cloud (which has never heard of local projects).
      if (targetsLocal && !engine && engineStarting) {
        return engineStarting
          .then(() => window.fetch(input, init))
          .catch(() => originalFetch(input, init));
      }

      if (engine && targetsLocal) {
        url = engine.base_url + url.slice(CLOUD_BASE.length);
        input = url;
      }

      if (engine && url.startsWith(engine.base_url)) {
        init = { ...(init || {}) };
        const headers = new Headers(init.headers || (typeof input !== 'string' && input.headers) || {});
        headers.set('Authorization', `Bearer ${engine.token}`);
        headers.set('X-Cloud-Token', localStorage.getItem('token') || '');
        init.headers = headers;
        input = url;
      }
    } catch (e) { /* never break a request over rewriting */ }
    return originalFetch(input, init);
  };
}

/**
 * Pushes the base URL into modules that keep their own module-level
 * API_BASE_URL (dataService, App). Dynamic imports avoid circular
 * dependency problems; each target is optional.
 */
async function applyBaseEverywhere(url) {
  try {
    const ds = await import('../services/dataService');
    if (ds.setApiBase) ds.setApiBase(url);
  } catch (e) { /* dataService not present in this build */ }
  try {
    const appMod = await import('../App');
    if (appMod.setAppApiBase) appMod.setAppApiBase(url);
  } catch (e) { /* App has no setter yet */ }
}